import { ArticleJsonLdProps } from "next-seo/lib/jsonld/article";

import {
    FRANCHISE_STRUCTURED_DATA,
    BRAND_STRUCTURED_DATA,
    LABEL_STRUCTURED_DATA,
    BRANDS_WITH_PILLARS,
} from "~constants";
import logoBloomberg from "~assets/bloomberg-logo-amp.png";
import logoSocial from "~assets/social-default.jpg";
import { Story as StoryType } from "~types/types";
import { assetPrefix } from "~utils/assetUtil";
import { iso8601Duration } from "~utils/durationUtil";
import { removeHTML } from "~utils/stringUtil";

import isPaywalled from "./isPaywalled";

const DEFAULT_IMAGE_URL = assetPrefix(logoSocial.src);
const LOGO_URL = assetPrefix(logoBloomberg.src);
const LOGO_WIDTH = 262;
const LOGO_HEIGHT = 60;

function getArticleAuthors(story: StoryType) {
    return story.authors?.map((author) => ({
        "@type": "Person",
        name: author.name,
    }));
}

function getArticleImages(story: StoryType): string[] {
    const { socialImageUrl = "", ledeImageUrl = "" } = story;

    return [socialImageUrl, ledeImageUrl, DEFAULT_IMAGE_URL].filter(Boolean);
}

export type StructuredData =
    | {
          name: string | null;
          url: string | null;
          "@type": string;
      }
    | undefined;

function getGoogleStructuredDataMentions(story: StoryType): StructuredData[] {
    const { label, brand, franchise } = story;

    const franchiseData = franchise
        ? FRANCHISE_STRUCTURED_DATA[franchise]
        : undefined;
    const brandData = BRAND_STRUCTURED_DATA[brand];
    const labelData =
        label && BRANDS_WITH_PILLARS.has(brand)
            ? LABEL_STRUCTURED_DATA[label]
            : undefined;
    return [franchiseData, brandData, labelData].filter(isValid);
}

function isValid(data: StructuredData) {
    return data && data.name && data.url;
}

function getArticleVideo(story: StoryType) {
    const {
        videoAttachments,
        seoHeadline: name,
        summary: description,
        publishedAt: uploadDate,
    } = story;

    if (!videoAttachments) {
        return null;
    }

    const videos = Object.values(videoAttachments);
    const [video] = videos;
    const stream = video?.streams?.find((s) => s.url);
    const duration = video?.duration;

    if (!stream?.url) {
        return null;
    }

    return {
        "@type": "VideoObject",
        name,
        description,
        uploadDate,
        duration: iso8601Duration(duration),
        contentURL: stream?.url,
        thumbnailURL: video?.thumbnail?.url?.replace("-1x-1", "110x62"),
        transcript: video?.transcript,
    };
}

export default function googleArticleJsonLd(
    story: StoryType
): ArticleJsonLdProps {
    const response: any = {
        "@context": "http://schema.org",
        "@type": "NewsArticle",
        authorName: getArticleAuthors(story),
        dateCreated: story.publishedAt,
        dateModified: story.updatedAt,
        datePublished: story.publishedAt,
        description: removeHTML(story?.seoDescription || null) || "",
        hasPart: {
            isAccessibleForFree: !isPaywalled(story),
        },
        isAccessibleForFree: !isPaywalled(story),
        isPartOf: {
            "@type": ["CreativeWork", "Product"],
            name: "Bloomberg",
            productID: "bloomberg.com:basic",
        },
        images: getArticleImages(story),
        mainEntityOfPage: story.url,
        mentions: getGoogleStructuredDataMentions(story),
        title: story.seoHeadline,
        video: getArticleVideo(story),
        publisher: {
            "@type": "Organization",
            name: "Bloomberg",
            url: "https://www.bloomberg.com",
            logo: {
                "@type": "ImageObject",
                url: LOGO_URL,
                width: LOGO_WIDTH,
                height: LOGO_HEIGHT,
            },
        },
        url: story.url,
    };

    if (isPaywalled(story)) {
        response.hasPart = {
            "@type": "WebPageElement",
            cssSelector: "#fortress-container-root",
            isAccessibleForFree: !isPaywalled(story),
        };
    }

    return response;
}
